import React, { useState } from 'react';
import { theme } from '../theme';
import Icons from './Icons';

// ---------------------------------------------------------------------------
// Saved Queries list (sidebar)
// Supports: open-tab indicator, delete confirmation, drag-to-reorder
// ---------------------------------------------------------------------------
export default function SavedQueriesList({ queries, onSelect, onDelete, onReorder, openSavedQueryIds = new Set() }) {
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [dragSourceId, setDragSourceId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const handleDeleteClick = (e, id) => {
    e.stopPropagation();
    setConfirmDeleteId(id);
  };

  const handleConfirmDelete = (e) => {
    e.stopPropagation();
    onDelete(confirmDeleteId);
    setConfirmDeleteId(null);
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setConfirmDeleteId(null);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (dragSourceId && dragSourceId !== targetId) onReorder(dragSourceId, targetId);
    setDragSourceId(null);
    setDragOverId(null);
  };

  if (!queries || queries.length === 0) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', color: theme.textMuted, fontSize: '12px' }}>
        No saved queries yet. Run a query then click Save.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {queries.map((q) => {
        const isOpen       = openSavedQueryIds.has(q.id);
        const isConfirming = confirmDeleteId === q.id;
        const isDragging   = dragSourceId === q.id;
        const isDropTarget = dragOverId === q.id && dragSourceId !== q.id;

        return (
          <div
            key={q.id}
            draggable
            onDragStart={() => setDragSourceId(q.id)}
            onDragEnd={() => { setDragSourceId(null); setDragOverId(null); }}
            onDragOver={(e) => { e.preventDefault(); setDragOverId(q.id); }}
            onDragLeave={() => setDragOverId(null)}
            onDrop={(e) => handleDrop(e, q.id)}
            onClick={() => !isConfirming && onSelect(q)}
            style={{
              padding: '8px 10px',
              borderRadius: '6px',
              cursor: isDragging ? 'grabbing' : 'grab',
              transition: 'background 0.15s, opacity 0.15s',
              opacity: isDragging ? 0.4 : 1,
              borderLeft: isDropTarget ? `2px solid ${theme.accent}` : '2px solid transparent',
              background: isConfirming ? `${theme.danger}0d` : 'transparent',
            }}
            onMouseEnter={(e) => {
              if (!isConfirming) e.currentTarget.style.background = theme.surfaceHover;
              const btn = e.currentTarget.querySelector('.del-btn');
              if (btn) btn.style.opacity = '1';
            }}
            onMouseLeave={(e) => {
              if (!isConfirming) e.currentTarget.style.background = 'transparent';
              const btn = e.currentTarget.querySelector('.del-btn');
              if (btn) btn.style.opacity = '0';
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '6px' }}>
              <span style={{
                fontWeight: 500, fontSize: '13px', flex: 1, minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                color: isConfirming ? theme.danger : theme.text,
              }}>
                {isConfirming ? `Delete "${q.name}"?` : q.name}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                {isOpen && !isConfirming && (
                  <span title="Open in a tab" style={{
                    fontSize: '10px', color: theme.accent, background: `${theme.accent}22`,
                    borderRadius: '3px', padding: '1px 5px', fontWeight: 600, letterSpacing: '0.02em',
                  }}>
                    open
                  </span>
                )}
                {isConfirming ? (
                  <>
                    <button
                      onClick={handleConfirmDelete}
                      title="Confirm delete"
                      style={{
                        background: theme.danger, border: 'none', color: '#fff',
                        cursor: 'pointer', padding: '2px 7px', borderRadius: '3px',
                        fontSize: '11px', fontWeight: 600,
                      }}
                    >
                      Delete
                    </button>
                    <button
                      onClick={handleCancelDelete}
                      title="Cancel"
                      style={{
                        background: 'none', border: `1px solid ${theme.border}`,
                        color: theme.textMuted, cursor: 'pointer',
                        padding: '2px 6px', borderRadius: '3px', fontSize: '11px',
                      }}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    className="del-btn"
                    onClick={(e) => handleDeleteClick(e, q.id)}
                    title="Delete"
                    style={{
                      background: 'none', border: 'none', color: theme.textMuted,
                      cursor: 'pointer', padding: '2px', opacity: 0,
                      transition: 'opacity 0.15s, color 0.15s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = theme.danger; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = theme.textMuted; }}
                  >
                    <Icons.X />
                  </button>
                )}
              </div>
            </div>
            {q.description && !isConfirming && (
              <div style={{ fontSize: '11px', color: theme.textMuted, marginTop: '2px' }}>{q.description}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
